import React, { useEffect, useMemo, useState } from "react";

const API = process.env.REACT_APP_API_URL || "http://localhost:3001";

// Normalisation simple
function norm(s) {
  return (s ?? "")
    .toString()
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function canonicalOperateurName(op) {
  const n = norm(op);
  if (!n) return "";
  return n.toUpperCase();
}

function parseDate(d) {
  if (!d) return null;
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function msToHuman(ms) {
  if (ms == null || !Number.isFinite(ms)) return "—";
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const mins = Math.floor(totalSec / 60);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);

  const hh = hours % 24;
  const mm = mins % 60;

  if (days > 0) return `${days}j ${hh}h ${mm}m`;
  if (hours > 0) return `${hours}h ${mm}m`;
  return `${mins}m`;
}

function avgMs(values) {
  if (!values.length) return null;
  const sum = values.reduce((a, b) => a + b, 0);
  return sum / values.length;
}

export default function StatsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [demandes, setDemandes] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${API}/api/demandes`);
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        setDemandes(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        setError("Impossible de charger les stats.");
        setDemandes([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const stats = useMemo(() => {
    const byOp = new Map();

    const getBucket = (opRaw) => {
      const key = canonicalOperateurName(opRaw);
      if (!key) return null;

      if (!byOp.has(key)) {
        byOp.set(key, {
          operateur: key, // ✅ affichage canonique (AMEL, LAMIA, etc.)
          nbChoisies: 0,
          eaciToRetour: [],
          flechageToRetourCds: [],
          dossierTreatment: [],
        });
      }
      return byOp.get(key);
    };

    for (const d of demandes) {
      const bucket = getBucket(d.operateur);

      // Nb demandes "choisies" par opérateur : on compte celles attribuées (operateur non vide)
      if (bucket) bucket.nbChoisies += 1;

      // EACI -> Retour EACI
      const eaciAt = parseDate(d.eaci_at);
      const retourEaciAt = parseDate(d.retour_eaci_at);
      if (bucket && eaciAt && retourEaciAt && retourEaciAt >= eaciAt) {
        bucket.eaciToRetour.push(retourEaciAt - eaciAt);
      }

      // Fléchage -> Retour CDS
      const flechageAt = parseDate(d.flechage_at);
      const retourCdsAt = parseDate(d.retour_cds_at);
      if (bucket && flechageAt && retourCdsAt && retourCdsAt >= flechageAt) {
        bucket.flechageToRetourCds.push(retourCdsAt - flechageAt);
      }

      // Temps de traitement du dossier : assigned_at -> closed_at
      const assignedAt = parseDate(d.assigned_at);
      const closedAt = parseDate(d.closed_at);
      if (bucket && assignedAt && closedAt && closedAt >= assignedAt) {
        bucket.dossierTreatment.push(closedAt - assignedAt);
      }
    }

    const rows = Array.from(byOp.values())
      .sort((a, b) => a.operateur.localeCompare(b.operateur))
      .map((s) => ({
        operateur: s.operateur,
        nbChoisies: s.nbChoisies,
        avgEaciRetour: msToHuman(avgMs(s.eaciToRetour)),
        nEaciRetour: s.eaciToRetour.length,
        avgFlechageRetourCds: msToHuman(avgMs(s.flechageToRetourCds)),
        nFlechageRetourCds: s.flechageToRetourCds.length,
        avgTraitementDossier: msToHuman(avgMs(s.dossierTreatment)),
        nTraitementDossier: s.dossierTreatment.length,
      }));

    const totals = {
      totalDemandes: demandes.length,
      totalAttribuees: demandes.filter((d) => canonicalOperateurName(d.operateur) !== "")
        .length,
    };

    return { rows, totals };
  }, [demandes]);

  return (
    <div className="content">
      <h1 style={{ marginTop: 0 }}>STATS</h1>

      {loading ? <p>Chargement...</p> : null}
      {error ? <p style={{ color: "red" }}>{error}</p> : null}

      {!loading && !error ? (
        <>
          <div style={{ marginBottom: 16, opacity: 0.9 }}>
            <div>
              Total demandes : <strong>{stats.totals.totalDemandes}</strong>
            </div>
            <div>
              Demandes attribuées : <strong>{stats.totals.totalAttribuees}</strong>
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", borderBottom: "1px solid #eee", padding: 8 }}>
                    Opérateur
                  </th>
                  <th style={{ textAlign: "right", borderBottom: "1px solid #eee", padding: 8 }}>
                    Demandes choisies
                  </th>
                  <th style={{ textAlign: "right", borderBottom: "1px solid #eee", padding: 8 }}>
                    Moy. EACI → Retour EACI
                  </th>
                  <th style={{ textAlign: "right", borderBottom: "1px solid #eee", padding: 8 }}>
                    N
                  </th>
                  <th style={{ textAlign: "right", borderBottom: "1px solid #eee", padding: 8 }}>
                    Moy. Fléchage → Retour CDS
                  </th>
                  <th style={{ textAlign: "right", borderBottom: "1px solid #eee", padding: 8 }}>
                    N
                  </th>
                  <th style={{ textAlign: "right", borderBottom: "1px solid #eee", padding: 8 }}>
                    Moy. traitement dossier
                  </th>
                  <th style={{ textAlign: "right", borderBottom: "1px solid #eee", padding: 8 }}>
                    N
                  </th>
                </tr>
              </thead>
              <tbody>
                {stats.rows.map((r) => (
                  <tr key={r.operateur}>
                    <td style={{ padding: 8, borderBottom: "1px solid #f5f5f5" }}>
                      {r.operateur}
                    </td>
                    <td style={{ padding: 8, borderBottom: "1px solid #f5f5f5", textAlign: "right" }}>
                      {r.nbChoisies}
                    </td>
                    <td style={{ padding: 8, borderBottom: "1px solid #f5f5f5", textAlign: "right" }}>
                      {r.avgEaciRetour}
                    </td>
                    <td style={{ padding: 8, borderBottom: "1px solid #f5f5f5", textAlign: "right" }}>
                      {r.nEaciRetour}
                    </td>
                    <td style={{ padding: 8, borderBottom: "1px solid #f5f5f5", textAlign: "right" }}>
                      {r.avgFlechageRetourCds}
                    </td>
                    <td style={{ padding: 8, borderBottom: "1px solid #f5f5f5", textAlign: "right" }}>
                      {r.nFlechageRetourCds}
                    </td>
                    <td style={{ padding: 8, borderBottom: "1px solid #f5f5f5", textAlign: "right" }}>
                      {r.avgTraitementDossier}
                    </td>
                    <td style={{ padding: 8, borderBottom: "1px solid #f5f5f5", textAlign: "right" }}>
                      {r.nTraitementDossier}
                    </td>
                  </tr>
                ))}
                {stats.rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ padding: 12, opacity: 0.8 }}>
                      Aucune donnée stats (pas de demandes attribuées).
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <p style={{ marginTop: 14, opacity: 0.75 }}>
            Note : les temps sont calculés à partir des timestamps enregistrés lors des changements de statut / import.
          </p>
        </>
      ) : null}
    </div>
  );
}
